import axios, { AxiosInstance } from 'axios';
import { auth } from './firebase';
import { userStore } from './userStore';

// Get API URL from environment or use production
const apiBaseURL = import.meta.env.VITE_API_URL || 'https://ride-connect-backend-git-main-ro-garcias-projects.vercel.app';

// Clean up the base URL
let baseURL = apiBaseURL.trim();
// Remove trailing slash
if (baseURL.endsWith('/')) {
  baseURL = baseURL.slice(0, -1);
}
// Add /api if not already present
if (!baseURL.endsWith('/api')) {
  baseURL = `${baseURL}/api`;
}

console.log('🔗 API Base URL:', baseURL);

const api = axios.create({
  baseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include Firebase token
api.interceptors.request.use(
  async (config) => {
    try {
      // Get current user - try auth.currentUser first, then fallback to userStore
      // userStore is set immediately after registration/login, before auth.currentUser is available
      let user = auth.currentUser;
      
      // If auth.currentUser is not available, try userStore (set immediately after registration)
      if (!user) {
        user = userStore.getUser();
        if (user) {
          console.log('✅ Using user from userStore (auth.currentUser not yet available)');
        }
      }

      if (user) {
        // Get token - use getIdToken(false) first to use cache, then force refresh if needed
        try {
          // Try cached token first (faster)
          let token = await user.getIdToken(false);
          
          // If no cached token, force refresh
          if (!token) {
            token = await user.getIdToken(true);
          }
          
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          } else {
            console.warn('⚠️ No token returned from getIdToken');
          }
        } catch (tokenError) {
          console.error('❌ Error getting token in interceptor:', tokenError);
        }
      }
    } catch (error) {
      console.error('Failed to get Firebase token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - could redirect to login
      console.error('Authentication failed');
    }
    return Promise.reject(error);
  }
);

// Helper function to upload avatar (multipart/form-data)
export const uploadAvatar = async (file: File): Promise<{ avatarUrl: string; user: any }> => {
  const formData = new FormData();
  formData.append('avatar', file);

  // Get auth token
  let user = auth.currentUser;
  if (!user) {
    user = userStore.getUser();
  }

  if (!user) {
    throw new Error('User not authenticated');
  }

  const token = await user.getIdToken();

  const response = await axios.post(`${baseURL}/users/avatar`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

// Helper function to add custom brand
export const addCustomBrand = async (brandName: string, category: string): Promise<any> => {
  const response = await api.post('/garage/brands', { brandName, category });
  return response.data;
};

// Helper function for forgot password
export const forgotPassword = async (email: string): Promise<any> => {
  const response = await api.post('/auth/forgot-password', { email });
  return response.data;
};

export default api;
